import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ConfirmDeleteModal } from '../../components/common/ConfirmDeleteModal';
import { Modal } from '../../components/common/Modal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  deleteAppUser,
  listDivisions,
  listIntranetUserDisplayNames,
  listIntranetUsers,
  type AccountStatus,
  type IntranetDivision,
  type IntranetUser,
  type IntranetUserRole,
} from '../../services/userAdministration';
import { listActiveRoles } from '../../services/roleAdministration';
import FilterSearchableSelect from '../../components/ui/FilterSearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import { fltSelClass } from '../../utils/alSelClass';
import { setFlashToast } from '../../utils/flashToast';

export default function IntranetUsersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<IntranetUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<IntranetUser | null>(null);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [nameOptions, setNameOptions] = useState<string[]>([]);
  const [nameOptionsLoading, setNameOptionsLoading] = useState(true);

  const [draftQName, setDraftQName] = useState('');
  const [draftQEmail, setDraftQEmail] = useState('');
  const [draftRole, setDraftRole] = useState<IntranetUserRole | ''>('');
  const [draftDivision, setDraftDivision] = useState<IntranetDivision | ''>('');
  const [draftStatus, setDraftStatus] = useState<AccountStatus | ''>('');

  const [qName, setQName] = useState('');
  const [qEmail, setQEmail] = useState('');
  const [role, setRole] = useState<IntranetUserRole | ''>('');
  const [division, setDivision] = useState<IntranetDivision | ''>('');
  const [status, setStatus] = useState<AccountStatus | ''>('');

  const [divisionOptions, setDivisionOptions] = useState<IntranetDivision[]>([]);

  const [viewId, setViewId] = useState<string | null>(null);

  const [pageSize, setPageSize] = useState(5);
  const [uiPage, setUiPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  /** Bumps when filters reset or Search is clicked so we refetch even if filter state is unchanged. */
  const [reloadToken, setReloadToken] = useState(0);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listIntranetUsers({
        pageNumber: Math.max(0, uiPage - 1),
        pageSize,
        name: qName.trim() || undefined,
        email: qEmail.trim() || undefined,
        role: role || undefined,
        division: division || undefined,
        active: status === '' ? null : status === 'Active',
      });
      if (!res.success) throw new Error(res.message);
      setRows(res.data.items);
      setTotalElements(res.data.totalElements);
      setTotalPages(res.data.totalPages);
      setUiPage(res.data.pageNumber + 1);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t('admin.intranetUsers.loadFailed');
      setFlashToast({ message, variant: 'error' });
      setRows([]);
      setTotalElements(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [division, pageSize, qEmail, qName, reloadToken, role, status, t, uiPage]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    void listActiveRoles().then((res) => {
      if (!alive) return;
      setRoleOptions(res.success ? res.data.map((r) => r.roleName.trim()).filter(Boolean) : []);
    });
    void listDivisions().then((res) => {
      if (!alive) return;
      setDivisionOptions(res.success ? res.data.map((d) => d.name.trim()).filter(Boolean) : []);
    });
    setNameOptionsLoading(true);
    void listIntranetUserDisplayNames().then((res) => {
      if (!alive) return;
      setNameOptions(res.success ? res.data : []);
      setNameOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  useEffect(() => {
    setDraftRole((cur) => {
      if (roleOptions.length === 0) return '';
      if (cur && !roleOptions.includes(cur)) return '';
      return cur;
    });
    setRole((cur) => {
      if (roleOptions.length === 0) return '';
      if (cur && !roleOptions.includes(cur)) return '';
      return cur;
    });
  }, [roleOptions]);

  useEffect(() => {
    setDraftDivision((cur) => {
      if (divisionOptions.length === 0) return '';
      if (cur && !divisionOptions.includes(cur)) return '';
      return cur;
    });
    setDivision((cur) => {
      if (divisionOptions.length === 0) return '';
      if (cur && !divisionOptions.includes(cur)) return '';
      return cur;
    });
  }, [divisionOptions]);

  const selected = rows.find((u) => u.id === viewId) ?? null;

  const runDelete = async (r: IntranetUser) => {
    setDeletingId(r.id);
    try {
      const res = await deleteAppUser(r.id);
      if (!res.success) throw new Error(res.message);
      setConfirmDelete(null);
      navigate(location.pathname + location.search, {
        replace: true,
        state: { toast: { message: t('admin.intranetUsers.deleteSuccess'), variant: 'success' } },
      });
      await loadList();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t('admin.intranetUsers.deleteFailed');
      setFlashToast({ message, variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const columns = useMemo<Column<IntranetUser>[]>(() => {
    return [
      { key: 'name', header: t('profile.name'), getValue: (r) => r.displayName, render: (r) => <span style={{ fontWeight: 600 }}>{r.displayName}</span> },
      {
        key: 'role',
        header: t('profile.userRoles'),
        getValue: (r) => r.rolesDisplay ?? r.role,
        render: (r) => <span>{r.rolesDisplay ?? r.role}</span>,
      },
      { key: 'division', header: t('profile.divisionUnit'), getValue: (r) => r.division },
      { key: 'email', header: t('profile.emailUsername'), getValue: (r) => r.email },
      {
        key: 'lastUpdatedAt',
        header: t('profile.lastUpdatedAt'),
        getValue: (r) => r.updatedAt,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.updatedAt}</span>,
      },
      {
        key: 'status',
        header: t('common.status'),
        getValue: (r) => r.status,
        render: (r) => (
          <span className={`pill ${r.status === 'Active' ? 'p-com' : 'p-arc'}`}>
            {r.status === 'Active' ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <Link className="act-b" to={`/admin/intranet-users/${encodeURIComponent(r.id)}/edit`} data-tip={t('admin.intranetUsers.editUser')} aria-label={t('admin.intranetUsers.editUser')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
            {r.status === 'Inactive' ? (
              <button
                className="act-b"
                type="button"
                disabled={deletingId === r.id}
                data-tip={t('admin.intranetUsers.deleteUser')}
                aria-label={t('admin.intranetUsers.deleteUser')}
                onClick={() => {
                  setConfirmDelete(r);
                }}
                style={{ color: 'var(--red)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [deletingId, t]);

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        {t('admin.intranetUsers.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('admin.intranetUsers.title')}</div>
        </div>
        <Link to="/admin/intranet-users/new" className="btn-create" style={{ textDecoration: 'none' }}>
          {t('admin.intranetUsers.addNewUser')}
        </Link>
      </div>

      <div className="flt-card">
        <div className="flt-grid">
          <div className="flt-f">
            <div className="flt-l">{t('profile.name')}</div>
            <FilterSearchableSelect
              value={draftQName}
              onChange={setDraftQName}
              placeholder={t('admin.intranetUsers.selectUserName')}
              options={nameOptions}
              loading={nameOptionsLoading}
              disabled={!nameOptionsLoading && nameOptions.length === 0}
              emptyMessage={t('common.noDataFound')}
              aria-label={t('admin.intranetUsers.filterByUserName')}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('profile.userRole')}</div>
            <select
              className={fltSelClass(draftRole)}
              value={draftRole}
              disabled={roleOptions.length === 0}
              onChange={(e) => setDraftRole(e.target.value as typeof draftRole)}
            >
              {roleOptions.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.intranetUsers.selectUserRole')}
                  </option>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('profile.divisionUnit')}</div>
            <select
              className={fltSelClass(draftDivision)}
              value={draftDivision}
              disabled={divisionOptions.length === 0}
              onChange={(e) => setDraftDivision(e.target.value as typeof draftDivision)}
            >
              {divisionOptions.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.intranetUsers.selectDivisionUnit')}
                  </option>
                  {divisionOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('profile.emailUsername')}</div>
            <input
              className="flt-inp"
              value={draftQEmail}
              onChange={(e) => setDraftQEmail(e.target.value)}
              placeholder={t('admin.intranetUsers.emailPlaceholder')}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('common.status')}</div>
            <select className={fltSelClass(draftStatus)} value={draftStatus} onChange={(e) => setDraftStatus(e.target.value as typeof draftStatus)}>
              <option value="" disabled hidden>
                {t('admin.intranetUsers.selectStatus')}
              </option>
              <option value="Active">{t('common.active')}</option>
              <option value="Inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setDraftQName('');
              setDraftQEmail('');
              setDraftRole('');
              setDraftDivision('');
              setDraftStatus('');
              setQName('');
              setQEmail('');
              setRole('');
              setDivision('');
              setStatus('');
              setUiPage(1);
              setPageSize(5);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setQName(draftQName);
              setQEmail(draftQEmail);
              setRole(draftRole);
              setDivision(draftDivision);
              setStatus(draftStatus);
              setUiPage(1);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      <DataTable
        title={t('admin.intranetUsers.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={5}
        rowsPerPageOptions={[5, 10, 15]}
        footerCountNoun={t('admin.intranetUsers.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: totalElements,
          totalPages,
          page: uiPage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />

      <Modal open={!!selected} title={t('admin.intranetUsers.userDetails')} onClose={() => setViewId(null)}>
        {selected ? (
          <>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--rs)', padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>{t('profile.name')}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.displayName}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--rs)', padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>{t('profile.userRoles')}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.rolesDisplay ?? selected.role}</div>
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--rs)', padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>{t('profile.divisionUnit')}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.division}</div>
                </div>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--rs)', padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>{t('profile.emailUsername')}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.email}</div>
              </div>
            </div>
            <div className="cn-footer cn-footer--plain">
              <button className="mfb mfb-dk" type="button" onClick={() => setViewId(null)}>
                {t('common.close')}
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      <ConfirmDeleteModal
        open={!!confirmDelete}
        title={t('admin.intranetUsers.deleteTitle')}
        entityLabel={t('admin.intranetUsers.entityLabel')}
        entityName={confirmDelete?.displayName ?? ''}
        deleting={!!deletingId}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) void runDelete(confirmDelete);
        }}
      />
    </>
  );
}
